import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ChevronDown, Printer, FileText, SplitSquareVertical,
  Plus, Trash2, Save, Check, X, CreditCard, Tag, AlertCircle,
  ChevronRight, Shield, Lock, Unlock, Wand2, Scale,
} from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'

// ─── Formatters ──────────────────────────────────────────────────────────────
const brl = v =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const parseMoney = s => {
  if (s === null || s === undefined || s === '') return null
  const clean = String(s).replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.')
  const v = parseFloat(clean)
  return isNaN(v) ? null : v
}

const fmtMoney = v => {
  if (v === null || v === undefined || v === '') return ''
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
}

const hojeISO = () => new Date().toISOString().slice(0, 10)

const addDiasISO = (iso, dias) => {
  if (!iso) return ''
  const d = new Date(iso + 'T12:00:00')
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}

const c2 = n => Math.round((n || 0) * 100) / 100          // 2 casas
const floor2 = n => Math.floor((n || 0) * 100) / 100

const diasEntre = (isoA, isoB) => {
  if (!isoA || !isoB) return null
  const a = new Date(isoA + 'T12:00:00'), b = new Date(isoB + 'T12:00:00')
  return Math.round((b - a) / 86400000)
}

// ─── Select ───────────────────────────────────────────────────────────────────
function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

// ─── PDF Viewer ───────────────────────────────────────────────────────────────
function PdfViewer({ pdfUrl, label, compradorNome, onClose, iframeRef }) {
  const print = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
        <span className="font-semibold text-gray-800">{label} — {compradorNome}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadPdf(pdfUrl, `${label} - ${compradorNome}.pdf`)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
            Salvar PDF
          </button>
          <button onClick={print} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
            <Printer size={15} /> Imprimir
          </button>
          <button onClick={onClose} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
            Fechar
          </button>
        </div>
      </div>
      <div style={{ height: '70vh' }}>
        <iframe ref={iframeRef} src={pdfUrl} title={label} style={{ width: '100%', height: '100%', border: 'none' }} />
      </div>
    </div>
  )
}

// ─── Money Input ──────────────────────────────────────────────────────────────
function MoneyInput({ value, onChange, placeholder = '0,00', className = '', disabled = false }) {
  const [raw, setRaw] = useState(value != null ? fmtMoney(value) : '')

  useEffect(() => {
    setRaw(value != null && value !== '' ? fmtMoney(value) : '')
  }, [value])

  return (
    <div className={`relative flex items-center ${className}`}>
      <span className="absolute left-3 text-gray-400 text-sm pointer-events-none">R$</span>
      <input
        type="text"
        value={raw}
        disabled={disabled}
        placeholder={placeholder}
        onChange={e => setRaw(e.target.value)}
        onBlur={() => {
          const v = parseMoney(raw)
          onChange(v)
          setRaw(v != null ? fmtMoney(v) : '')
        }}
        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 text-right tabular-nums"
      />
    </div>
  )
}

// ─── Resumo Card ──────────────────────────────────────────────────────────────
function ResumoCard({ resumo, descontoAplicado }) {
  if (!resumo) return null
  const valorFinal = resumo.valor_comprador - (descontoAplicado || 0)
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="text-xs text-gray-400 mb-0.5">Valor Total</div>
        <div className="font-semibold text-gray-800">{brl(resumo.valor_total)}</div>
      </div>
      {resumo.comissao_compra > 0 && (
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <div className="text-xs text-gray-400 mb-0.5">Com. Compra</div>
          <div className="font-semibold text-gray-700">{brl(resumo.comissao_compra)}</div>
        </div>
      )}
      {resumo.desconto > 0 && (
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <div className="text-xs text-amber-500 mb-0.5">Desconto (lotes)</div>
          <div className="font-semibold text-amber-700">-{brl(resumo.desconto)}</div>
        </div>
      )}
      <div className={`rounded-lg p-3 border col-span-2 sm:col-span-1 ${
        descontoAplicado > 0
          ? 'bg-blue-50 border-blue-300'
          : 'bg-blue-50 border-blue-200'
      }`}>
        <div className="text-xs text-blue-500 mb-0.5">
          {descontoAplicado > 0 ? 'Valor Negociado' : 'Valor do Comprador'}
        </div>
        <div className="font-bold text-blue-700 text-base">{brl(valorFinal)}</div>
        {descontoAplicado > 0 && (
          <div className="text-xs text-blue-400 mt-0.5">
            original: {brl(resumo.valor_comprador)}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Negotiation Form ─────────────────────────────────────────────────────────
function NegociacaoForm({ orgId, leilaoId, compradorId, modalidade, onSaved }) {
  const modalKey = modalidade || ''

  const [resumo, setResumo] = useState(null)
  const [loadingResumo, setLoadingResumo] = useState(true)
  const [loadingNeg, setLoadingNeg] = useState(true)

  // Desconto negociado
  const [descontoTipo, setDescontoTipo] = useState('pct') // 'pct' | 'valor'
  const [descontoPct, setDescontoPct] = useState(null)
  const [descontoValor, setDescontoValor] = useState(null)

  // Pagamento
  const [entradaValor, setEntradaValor] = useState(null)
  const [entradaData, setEntradaData] = useState('')
  const [parcelas, setParcelas] = useState([])
  const [observacao, setObservacao] = useState('')

  // Gerador de parcelas
  const [genDataBase, setGenDataBase] = useState('')
  const [genIntervalo, setGenIntervalo] = useState(30)
  const [genN, setGenN] = useState(12)
  const [genPrimeiraAVista, setGenPrimeiraAVista] = useState(true)
  const [genPrimeiraDias, setGenPrimeiraDias] = useState(30)
  // Escalonamento opcional: primeiras N parcelas com valor fixo
  const [escUsar, setEscUsar] = useState(false)
  const [escQtd, setEscQtd] = useState(3)
  const [escValor, setEscValor] = useState(null)

  // Estado
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const loadData = useCallback(async () => {
    if (!orgId || !leilaoId || !compradorId) return
    setLoadingResumo(true)
    setLoadingNeg(true)
    const [res, neg] = await Promise.allSettled([
      mapasService.getContratoUnificadoResumo(orgId, leilaoId, compradorId, modalidade || null),
      mapasService.getNegociacao(orgId, leilaoId, compradorId, modalKey),
    ])
    if (res.status === 'fulfilled') setResumo(res.value)
    setLoadingResumo(false)

    if (neg.status === 'fulfilled') {
      const n = neg.value
      const pct = n.desconto_negociado_pct ?? null
      const val = n.desconto_negociado_valor ?? null
      setDescontoPct(pct)
      setDescontoValor(val)
      setDescontoTipo(pct != null && pct > 0 ? 'pct' : 'valor')
      setEntradaValor(n.entrada_valor ?? null)
      setEntradaData(n.entrada_data ?? '')
      setParcelas(n.parcelas ?? [])
      setObservacao(n.observacao ?? '')
    } else {
      setDescontoPct(null); setDescontoValor(null)
      setEntradaValor(null); setEntradaData('')
      setParcelas([]); setObservacao('')
    }
    setLoadingNeg(false)
  }, [orgId, leilaoId, compradorId, modalidade, modalKey])

  useEffect(() => { loadData() }, [loadData])

  // ── Cálculos derivados ────────────────────────────────────────────────────
  const baseComprador = resumo?.valor_comprador ?? 0
  const baseTotal = resumo?.valor_total ?? 0

  const descontoAplicado = (() => {
    if (descontoTipo === 'pct' && descontoPct != null) {
      return Math.round(baseTotal * descontoPct / 100 * 100) / 100
    }
    return descontoValor ?? 0
  })()

  const pctEquivalente = baseTotal > 0 && descontoAplicado > 0
    ? (descontoAplicado / baseTotal * 100).toFixed(2)
    : null

  const valorLiquido = baseComprador - descontoAplicado

  const totalPago = (entradaValor || 0) + parcelas.reduce((s, p) => s + (p.valor || 0), 0)
  const restante = valorLiquido - (entradaValor || 0)
  const diferenca = totalPago - valorLiquido
  const balanceado = Math.abs(diferenca) < 0.02

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleDescontoPctChange = v => {
    setDescontoPct(v)
    setDescontoValor(null)
  }

  const handleDescontoValorChange = v => {
    setDescontoValor(v)
    setDescontoPct(null)
  }

  const addParcela = () => {
    setParcelas(prev => {
      const last = prev[prev.length - 1]
      const proxData = last?.data
        ? addDiasISO(last.data, Number(genIntervalo) || 30)
        : ''
      return [...prev, { numero: prev.length + 1, valor: null, data: proxData, travada: false }]
    })
  }

  const removeParcela = idx => {
    setParcelas(prev => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, numero: i + 1 })))
  }

  const updateParcela = (idx, field, val) => {
    setParcelas(prev => prev.map((p, i) => i === idx ? { ...p, [field]: val } : p))
  }

  const toggleTravada = idx => {
    setParcelas(prev => prev.map((p, i) => i === idx ? { ...p, travada: !p.travada } : p))
  }

  const limparParcelas = () => setParcelas([])

  // Gera N parcelas de uma vez. Opcionalmente as primeiras `escQtd` com valor
  // fixo (`escValor`) e o restante dividido igualmente. A última parcela livre
  // absorve os centavos pra fechar o total exato.
  const gerarParcelas = () => {
    const n = Math.max(1, Math.min(120, parseInt(genN) || 1))
    if (restante <= 0.01) return
    const base = genDataBase || hojeISO()
    const intervalo = Number(genIntervalo) || 30
    const off0 = genPrimeiraAVista ? 0 : (parseInt(genPrimeiraDias) || 0)

    const escN = escUsar && escValor > 0 ? Math.min(parseInt(escQtd) || 0, n) : 0
    let valores
    if (escN > 0) {
      const nResto = n - escN
      if (nResto <= 0) {
        valores = Array.from({ length: n }, () => c2(escValor))
      } else {
        const vResto = floor2((restante - escN * escValor) / nResto)
        valores = Array.from({ length: n }, (_, i) => (i < escN ? c2(escValor) : vResto))
      }
    } else {
      const v = floor2(restante / n)
      valores = Array.from({ length: n }, () => v)
    }
    // Ajuste de centavos na última parcela
    const soma = c2(valores.reduce((s, x) => s + x, 0))
    valores[n - 1] = c2(valores[n - 1] + (restante - soma))

    setParcelas(valores.map((valor, i) => ({
      numero: i + 1,
      valor,
      data: addDiasISO(base, off0 + intervalo * i),
      travada: false,
    })))
  }

  // Redistribui o que sobra (restante − parcelas travadas) igualmente entre as
  // parcelas NÃO travadas. Deixa o usuário fixar as primeiras e fechar o resto.
  const redistribuirRestante = () => {
    const somaTravadas = parcelas.filter(p => p.travada).reduce((s, p) => s + (p.valor || 0), 0)
    const alvo = c2(restante - somaTravadas)
    const livres = parcelas.filter(p => !p.travada).length
    if (livres === 0) return
    const v = floor2(alvo / livres)
    let vistos = 0
    setParcelas(prev => prev.map(p => {
      if (p.travada) return p
      vistos += 1
      const val = vistos === livres ? c2(alvo - v * (livres - 1)) : v
      return { ...p, valor: val }
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const finalPct = descontoTipo === 'pct' ? descontoPct : (pctEquivalente ? parseFloat(pctEquivalente) : null)
      const finalVal = descontoAplicado > 0 ? descontoAplicado : null
      await mapasService.saveNegociacao(orgId, {
        leilao_id: parseInt(leilaoId),
        comprador_id: parseInt(compradorId),
        modalidade: modalKey,
        desconto_negociado_pct: finalPct,
        desconto_negociado_valor: finalVal,
        entrada_valor: entradaValor || null,
        entrada_data: entradaData || null,
        parcelas: parcelas.filter(p => p.valor).map(p => ({
          numero: p.numero,
          valor: p.valor,
          data: p.data || null,
        })),
        observacao: observacao || null,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      if (onSaved) onSaved()
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.detail || err.message))
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    if (!confirm('Limpar toda a negociação salva?')) return
    setDeleting(true)
    try {
      await mapasService.deleteNegociacao(orgId, leilaoId, compradorId, modalKey)
    } catch {
      // 404 = já não existe, ok
    } finally {
      setDescontoPct(null); setDescontoValor(null)
      setEntradaValor(null); setEntradaData('')
      setParcelas([]); setObservacao('')
      setDeleting(false)
    }
  }

  const loading = loadingResumo || loadingNeg

  if (loading) return (
    <div className="flex items-center justify-center py-10 text-sm text-gray-400">
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-300 border-t-blue-600 mr-2" />
      Carregando dados financeiros...
    </div>
  )

  return (
    <div className="space-y-5">

      {/* ── Resumo Financeiro ─────────────────────────────────────────────── */}
      <ResumoCard resumo={resumo} descontoAplicado={descontoAplicado} />

      {/* ── Desconto Negociado ────────────────────────────────────────────── */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Tag size={14} className="text-amber-600" />
          <span className="text-sm font-semibold text-amber-800">Desconto Negociado</span>
          <span className="text-xs text-amber-500 ml-1">aplicado sobre o valor total dos lotes</span>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Toggle % / R$ */}
          <div className="flex rounded-lg border border-amber-300 overflow-hidden text-sm font-medium">
            <button
              onClick={() => { setDescontoTipo('pct'); setDescontoValor(null) }}
              className={`px-4 py-2 transition-colors ${descontoTipo === 'pct' ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 hover:bg-amber-100'}`}
            >
              %
            </button>
            <button
              onClick={() => { setDescontoTipo('valor'); setDescontoPct(null) }}
              className={`px-4 py-2 transition-colors ${descontoTipo === 'valor' ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 hover:bg-amber-100'}`}
            >
              R$
            </button>
          </div>

          {descontoTipo === 'pct' ? (
            <div className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={descontoPct ?? ''}
                  onChange={e => handleDescontoPctChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                  placeholder="0,00"
                  className="w-28 pr-7 pl-3 py-2 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 text-right tabular-nums bg-white"
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-amber-400 text-sm pointer-events-none">%</span>
              </div>
              {descontoAplicado > 0 && (
                <div className="flex items-center gap-1.5 bg-white rounded-lg border border-amber-300 px-3 py-2 text-sm">
                  <ChevronRight size={13} className="text-amber-400" />
                  <span className="text-amber-700 font-semibold tabular-nums">-{brl(descontoAplicado)}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <MoneyInput
                value={descontoValor}
                onChange={handleDescontoValorChange}
                className="w-44"
              />
              {descontoAplicado > 0 && pctEquivalente && (
                <div className="flex items-center gap-1.5 bg-white rounded-lg border border-amber-300 px-3 py-2 text-sm">
                  <ChevronRight size={13} className="text-amber-400" />
                  <span className="text-amber-700 tabular-nums">{pctEquivalente}%</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Valor líquido resultante */}
        {descontoAplicado > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-amber-700">
              Valor do comprador após desconto:
            </span>
            <span className="font-bold text-amber-900 tabular-nums text-base">{brl(valorLiquido)}</span>
          </div>
        )}
      </div>

      {/* ── Forma de Pagamento ────────────────────────────────────────────── */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard size={14} className="text-blue-600" />
          <span className="text-sm font-semibold text-blue-800">Forma de Pagamento</span>
          <span className="text-xs text-blue-400 ml-1 tabular-nums">sobre {brl(valorLiquido)}</span>
        </div>

        {/* Entrada */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">Valor de Entrada</label>
            <MoneyInput value={entradaValor} onChange={setEntradaValor} className="w-full" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500">Data da Entrada</label>
            <input
              type="date"
              value={entradaData}
              onChange={e => setEntradaData(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>
        </div>

        {/* Restante após entrada */}
        {entradaValor > 0 && (
          <div className="flex items-center gap-2 text-xs text-blue-600 bg-white border border-blue-200 rounded-lg px-3 py-2">
            <span>Restante após entrada:</span>
            <span className="font-bold tabular-nums">{brl(restante)}</span>
          </div>
        )}

        {/* Parcelas */}
        <div className="space-y-3">
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Parcelas</label>

          {/* ── Gerador ─────────────────────────────────────────────────── */}
          <div className="bg-white border border-blue-100 rounded-xl p-3.5 space-y-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-700">
              <Wand2 size={13} /> Gerar parcelas
            </div>

            <div className="flex flex-wrap items-end gap-x-4 gap-y-2.5">
              <div className="space-y-1">
                <label className="block text-[11px] text-gray-400 font-medium">Nº de parcelas</label>
                <input
                  type="number" min="1" max="120"
                  value={genN}
                  onChange={e => setGenN(e.target.value)}
                  className="w-20 border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] text-gray-400 font-medium">1ª parcela</label>
                <div className="flex items-center gap-1.5">
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                    <button type="button" onClick={() => setGenPrimeiraAVista(true)}
                      className={`px-2.5 py-1.5 font-medium transition-colors ${genPrimeiraAVista ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                      À vista
                    </button>
                    <button type="button" onClick={() => setGenPrimeiraAVista(false)}
                      className={`px-2.5 py-1.5 font-medium border-l border-gray-300 transition-colors ${!genPrimeiraAVista ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                      Em dias
                    </button>
                  </div>
                  {!genPrimeiraAVista && (
                    <input
                      type="number" min="1"
                      value={genPrimeiraDias}
                      onChange={e => setGenPrimeiraDias(e.target.value)}
                      className="w-16 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] text-gray-400 font-medium">Intervalo</label>
                <select
                  value={genIntervalo}
                  onChange={e => setGenIntervalo(Number(e.target.value))}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={7}>7 dias</option>
                  <option value={15}>15 dias</option>
                  <option value={30}>30 dias</option>
                  <option value={45}>45 dias</option>
                  <option value={60}>60 dias</option>
                  <option value={90}>90 dias</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-[11px] text-gray-400 font-medium">Data base</label>
                <input
                  type="date"
                  value={genDataBase}
                  onChange={e => setGenDataBase(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Escalonamento opcional */}
            <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer select-none">
              <input type="checkbox" checked={escUsar} onChange={e => setEscUsar(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="flex flex-wrap items-center gap-1.5 leading-6">
                As primeiras
                <input type="number" min="1" value={escQtd} disabled={!escUsar}
                  onChange={e => setEscQtd(e.target.value)}
                  className="w-14 border border-gray-300 rounded-md px-1.5 py-0.5 text-sm text-center tabular-nums disabled:bg-gray-100 disabled:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                parcela(s) de
                <span className={escUsar ? '' : 'opacity-40 pointer-events-none'}>
                  <MoneyInput value={escValor} onChange={setEscValor} className="w-32 inline-block" />
                </span>
                <span className="text-gray-400">— o restante é dividido igualmente.</span>
              </span>
            </label>

            <div className="flex items-center justify-between pt-0.5">
              <span className="text-[11px] text-gray-400 tabular-nums">
                {restante > 0.01 ? `${genN || 0}x ≈ ${brl(floor2((restante - (escUsar && escValor > 0 ? Math.min(parseInt(escQtd) || 0, parseInt(genN) || 1) : 0) * (escValor || 0)) / Math.max(1, (parseInt(genN) || 1) - (escUsar && escValor > 0 ? Math.min(parseInt(escQtd) || 0, parseInt(genN) || 1) : 0))))}` : 'Defina um valor a parcelar'}
              </span>
              <button
                type="button"
                onClick={gerarParcelas}
                disabled={restante <= 0.01}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-lg transition-colors"
              >
                <Wand2 size={13} /> Gerar {parseInt(genN) || ''} parcelas
              </button>
            </div>
          </div>

          {/* ── Lista de parcelas ───────────────────────────────────────── */}
          {parcelas.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs text-gray-500 tabular-nums">
                {parcelas.length} parcela{parcelas.length > 1 ? 's' : ''}
                <span className={`ml-2 font-semibold ${balanceado ? 'text-green-600' : 'text-orange-500'}`}>
                  {balanceado ? 'fecha o total' : `${diferenca > 0 ? '+' : ''}${brl(diferenca)}`}
                </span>
              </span>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={redistribuirRestante}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg transition-colors">
                  <Scale size={12} /> Redistribuir restante
                </button>
                <button type="button" onClick={addParcela}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 text-gray-600 border border-gray-300 rounded-lg transition-colors">
                  <Plus size={12} /> Parcela
                </button>
                <button type="button" onClick={limparParcelas}
                  className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Limpar todas">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          )}

          {parcelas.length === 0 && (
            <div className="text-xs text-gray-400 italic py-1 pl-1">Nenhuma parcela — use o gerador acima.</div>
          )}

          <div className="space-y-1.5">
            {parcelas.map((p, idx) => {
              const refData = entradaData || genDataBase
              const dias = diasEntre(refData, p.data)
              return (
                <div key={idx} className={`grid grid-cols-[2.5rem_auto_1fr_1fr_auto] items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors ${
                  p.travada ? 'bg-blue-50/60 border-blue-200' : 'bg-white border-gray-200'
                }`}>
                  <span className="text-[11px] text-gray-400 font-semibold tabular-nums text-center">
                    {p.numero}/{parcelas.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleTravada(idx)}
                    title={p.travada ? 'Travada — não muda ao redistribuir' : 'Livre — recebe o rateio ao redistribuir'}
                    className={`p-1.5 rounded-lg transition-colors ${
                      p.travada ? 'text-blue-600 bg-blue-100' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'
                    }`}
                  >
                    {p.travada ? <Lock size={13} /> : <Unlock size={13} />}
                  </button>
                  <MoneyInput value={p.valor} onChange={val => updateParcela(idx, 'valor', val)} />
                  <div className="flex items-center gap-1.5">
                    <input
                      type="date"
                      value={p.data || ''}
                      onChange={e => updateParcela(idx, 'data', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {dias != null && dias >= 0 && (
                      <span className="text-[10px] text-gray-400 whitespace-nowrap tabular-nums">{dias}d</span>
                    )}
                  </div>
                  <button
                    onClick={() => removeParcela(idx)}
                    className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Observação */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500">Observação</label>
          <input
            type="text"
            value={observacao}
            onChange={e => setObservacao(e.target.value)}
            placeholder="Ex: Condições acordadas em reunião do dia..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
      </div>

      {/* ── Totalização ───────────────────────────────────────────────────── */}
      {(entradaValor > 0 || parcelas.length > 0) && (
        <div className={`rounded-xl border p-3 flex items-center justify-between text-sm ${
          balanceado
            ? 'bg-green-50 border-green-200'
            : diferenca > 0
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="flex items-center gap-3">
            {balanceado ? (
              <Check size={16} className="text-green-600 shrink-0" />
            ) : (
              <AlertCircle size={16} className={`shrink-0 ${diferenca > 0 ? 'text-yellow-600' : 'text-orange-600'}`} />
            )}
            <span className={balanceado ? 'text-green-700' : diferenca > 0 ? 'text-yellow-700' : 'text-orange-700'}>
              Total negociado: <strong className="tabular-nums">{brl(totalPago)}</strong>
              {!balanceado && (
                <span className="ml-2 font-normal text-xs">
                  ({diferenca > 0 ? '+' : ''}{brl(diferenca)} vs valor do comprador)
                </span>
              )}
              {balanceado && <span className="ml-2 font-normal text-xs">— valores balanceados</span>}
            </span>
          </div>
        </div>
      )}

      {/* ── Ações ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={handleClear}
          disabled={deleting}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-400 hover:text-red-600 hover:bg-red-50 border border-gray-200 rounded-lg transition-colors"
        >
          <X size={13} /> Limpar negociação
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
            saved
              ? 'bg-green-600 text-white'
              : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white'
          }`}
        >
          {saved
            ? <><Check size={14} /> Salvo!</>
            : saving
              ? 'Salvando...'
              : <><Save size={14} /> Salvar Negociação</>
          }
        </button>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ContratoUnificado() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [compradores, setCompradores] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [compradorId, setCompradorId] = useState('')
  const [loadingL, setLoadingL] = useState(true)
  const [loadingC, setLoadingC] = useState(false)
  const [separarModalidade, setSepararModalidade] = useState(false)
  const [showNegociacao, setShowNegociacao] = useState(false)
  const [incluirDefesa, setIncluirDefesa] = useState(false)
  const [formKey, setFormKey] = useState(0)

  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const iframeRef = useRef(null)

  const [loading3Vias, setLoading3Vias] = useState(false)
  const [pdf3ViasUrl, setPdf3ViasUrl] = useState(null)
  const iframe3ViasRef = useRef(null)

  const [loadingAVista, setLoadingAVista] = useState(false)
  const [loadingPrazo, setLoadingPrazo] = useState(false)
  const [pdfAVistaUrl, setPdfAVistaUrl] = useState(null)
  const [pdfPrazoUrl, setPdfPrazoUrl] = useState(null)
  const iframeAVistaRef = useRef(null)
  const iframePrazoRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setCompradores([]); return }
    setLoadingC(true)
    mapasService.getCompradores(orgId, leilaoId)
      .then(list => { setCompradores(list); setCompradorId(list.length ? String(list[0].id) : '') })
      .catch(() => setCompradores([]))
      .finally(() => setLoadingC(false))
  }, [orgId, leilaoId])

  const revokeAll = () => {
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
    if (pdfAVistaUrl) { URL.revokeObjectURL(pdfAVistaUrl); setPdfAVistaUrl(null) }
    if (pdfPrazoUrl) { URL.revokeObjectURL(pdfPrazoUrl); setPdfPrazoUrl(null) }
  }

  useEffect(() => {
    revokeAll()
    setFormKey(k => k + 1)
  }, [leilaoId, compradorId]) // eslint-disable-line react-hooks/exhaustive-deps

  const compradorNome = compradores.find(c => String(c.id) === compradorId)?.nome || ''

  const handleGerar = async () => {
    if (!orgId || !leilaoId || !compradorId) return
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getContratoUnificadoPdf(orgId, leilaoId, compradorId, null, incluirDefesa)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfUrl(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })))
    } catch (err) { alert('Erro: ' + (err.response?.data?.detail || err.message)) }
    finally { setLoadingPdf(false) }
  }

  const handleGerarAVista = async () => {
    if (!orgId || !leilaoId || !compradorId) return
    setLoadingAVista(true)
    try {
      const blob = await mapasService.getContratoUnificadoPdf(orgId, leilaoId, compradorId, 'A_VISTA', incluirDefesa)
      if (pdfAVistaUrl) URL.revokeObjectURL(pdfAVistaUrl)
      setPdfAVistaUrl(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })))
    } catch (err) { alert('Erro (À Vista): ' + (err.response?.data?.detail || err.message)) }
    finally { setLoadingAVista(false) }
  }

  const handleGerarPrazo = async () => {
    if (!orgId || !leilaoId || !compradorId) return
    setLoadingPrazo(true)
    try {
      const blob = await mapasService.getContratoUnificadoPdf(orgId, leilaoId, compradorId, 'PRAZO', incluirDefesa)
      if (pdfPrazoUrl) URL.revokeObjectURL(pdfPrazoUrl)
      setPdfPrazoUrl(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })))
    } catch (err) { alert('Erro (Parcelado): ' + (err.response?.data?.detail || err.message)) }
    finally { setLoadingPrazo(false) }
  }

  const handleGerarAmbos = () => Promise.all([handleGerarAVista(), handleGerarPrazo()])

  const handleGerar3Vias = async () => {
    if (!orgId || !leilaoId || !compradorId) return
    setLoading3Vias(true)
    try {
      const blob = await mapasService.getContrato3ViasPdf(orgId, leilaoId, compradorId)
      if (pdf3ViasUrl) URL.revokeObjectURL(pdf3ViasUrl)
      setPdf3ViasUrl(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })))
    } catch (err) { alert('Erro (3 Vias): ' + (err.response?.data?.detail || err.message)) }
    finally { setLoading3Vias(false) }
  }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador.
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-violet-600" />
        <h1 className="text-lg font-semibold text-gray-800">Contrato Unificado</h1>
      </div>

      {/* ── Controles ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Selects */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          <div className="p-4">
            <label className="text-xs text-gray-400 font-medium block mb-1.5">Leilão</label>
            <div className="relative">
              <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                className="w-full appearance-none border-0 bg-transparent text-sm font-medium text-gray-800 focus:outline-none pr-6 disabled:text-gray-400 cursor-pointer">
                {loadingL
                  ? <option>Carregando...</option>
                  : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
              </select>
              <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="p-4">
            <label className="text-xs text-gray-400 font-medium block mb-1.5">Cliente</label>
            <div className="relative">
              <select value={compradorId} onChange={e => setCompradorId(e.target.value)} disabled={loadingC || !leilaoId}
                className="w-full appearance-none border-0 bg-transparent text-sm font-medium text-gray-800 focus:outline-none pr-6 disabled:text-gray-400 cursor-pointer">
                {compradores.map(c => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
              </select>
              <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Barra de ações */}
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 flex flex-wrap items-center gap-2">
          {/* Toggles de opções */}
          <button
            onClick={() => setSepararModalidade(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
              separarModalidade
                ? 'bg-violet-600 border-violet-600 text-white shadow-sm'
                : 'bg-white border-gray-200 text-gray-500 hover:border-violet-300 hover:text-violet-600'
            }`}
          >
            <SplitSquareVertical size={13} />
            Separar modalidade
          </button>
          <button
            onClick={() => setIncluirDefesa(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
              incluirDefesa
                ? 'bg-slate-600 border-slate-600 text-white shadow-sm'
                : 'bg-white border-gray-200 text-gray-500 hover:border-slate-300 hover:text-slate-600'
            }`}
          >
            <Shield size={13} />
            Incluir Defesa
          </button>

          <div className="w-px h-5 bg-gray-200 mx-1 hidden sm:block" />

          {/* Botões de gerar */}
          {!separarModalidade ? (
            <>
            <button onClick={handleGerar} disabled={!compradorId || loadingPdf}
              className="flex items-center gap-2 px-4 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors shadow-sm">
              <FileText size={13} />
              {loadingPdf ? 'Gerando...' : 'Gerar Contrato'}
            </button>
            <button onClick={handleGerar3Vias} disabled={!compradorId || loading3Vias}
              className="flex items-center gap-2 px-4 py-1.5 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors shadow-sm">
              <FileText size={13} />
              {loading3Vias ? 'Gerando...' : '3 Vias'}
            </button>
            </>
          ) : (
            <div className="flex items-center gap-1.5 flex-wrap">
              <button onClick={handleGerarAmbos} disabled={!compradorId || loadingAVista || loadingPrazo}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-xs font-semibold transition-colors shadow-sm">
                <SplitSquareVertical size={13} />
                {(loadingAVista || loadingPrazo) ? 'Gerando...' : 'Gerar Ambos'}
              </button>
              <button onClick={handleGerarAVista} disabled={!compradorId || loadingAVista}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-xs font-medium transition-colors">
                {loadingAVista ? '...' : 'À Vista'}
              </button>
              <button onClick={handleGerarPrazo} disabled={!compradorId || loadingPrazo}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-xs font-medium transition-colors">
                {loadingPrazo ? '...' : 'Parcelado'}
              </button>
            </div>
          )}

          {/* Negociação — fica à direita */}
          {compradorId && (
            <button
              onClick={() => setShowNegociacao(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ml-auto ${
                showNegociacao
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:border-blue-400'
              }`}
            >
              <CreditCard size={13} />
              Desconto &amp; Negociação
            </button>
          )}
        </div>
      </div>

      {/* ── Painel de Negociação ───────────────────────────────────────────── */}
      {showNegociacao && compradorId && leilaoId && (
        <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-blue-100 bg-blue-50">
            <CreditCard size={15} className="text-blue-600" />
            <h2 className="text-sm font-semibold text-blue-900">Desconto &amp; Negociação à Parte</h2>
            {!separarModalidade && (
              <span className="text-xs text-blue-400 ml-1">— Contrato Unificado</span>
            )}
            <span className="ml-auto text-xs text-blue-400 font-medium">{compradorNome}</span>
          </div>

          <div className="p-5">
            {!separarModalidade ? (
              <NegociacaoForm
                key={`uni-${leilaoId}-${compradorId}-${formKey}`}
                orgId={orgId}
                leilaoId={leilaoId}
                compradorId={compradorId}
                modalidade=""
                onSaved={handleGerar}
              />
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-blue-100">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">À Vista</span>
                  </div>
                  <NegociacaoForm
                    key={`av-${leilaoId}-${compradorId}-${formKey}`}
                    orgId={orgId}
                    leilaoId={leilaoId}
                    compradorId={compradorId}
                    modalidade="A_VISTA"
                    onSaved={handleGerarAVista}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-emerald-100">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Parcelado</span>
                  </div>
                  <NegociacaoForm
                    key={`pr-${leilaoId}-${compradorId}-${formKey}`}
                    orgId={orgId}
                    leilaoId={leilaoId}
                    compradorId={compradorId}
                    modalidade="PRAZO"
                    onSaved={handleGerarPrazo}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Viewers ───────────────────────────────────────────────────────── */}
      {!separarModalidade && pdfUrl && (
        <PdfViewer
          pdfUrl={pdfUrl}
          label="Contrato Unificado"
          compradorNome={compradorNome}
          onClose={() => { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }}
          iframeRef={iframeRef}
        />
      )}

      {pdf3ViasUrl && (
        <PdfViewer
          pdfUrl={pdf3ViasUrl}
          label="Contrato 3 Vias"
          compradorNome={compradorNome}
          onClose={() => { URL.revokeObjectURL(pdf3ViasUrl); setPdf3ViasUrl(null) }}
          iframeRef={iframe3ViasRef}
        />
      )}

      {separarModalidade && (
        <div className="space-y-4">
          {!pdfAVistaUrl && !pdfPrazoUrl && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
              Clique em "Gerar Ambos", "Gerar À Vista" ou "Gerar Parcelado" para visualizar os contratos
            </div>
          )}
          {pdfAVistaUrl && (
            <PdfViewer
              pdfUrl={pdfAVistaUrl}
              label="Contrato Unificado — À Vista"
              compradorNome={compradorNome}
              onClose={() => { URL.revokeObjectURL(pdfAVistaUrl); setPdfAVistaUrl(null) }}
              iframeRef={iframeAVistaRef}
            />
          )}
          {pdfPrazoUrl && (
            <PdfViewer
              pdfUrl={pdfPrazoUrl}
              label="Contrato Unificado — Parcelado"
              compradorNome={compradorNome}
              onClose={() => { URL.revokeObjectURL(pdfPrazoUrl); setPdfPrazoUrl(null) }}
              iframeRef={iframePrazoRef}
            />
          )}
        </div>
      )}

      {!separarModalidade && !pdfUrl && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione um leilão e comprador, depois clique em "Gerar Contrato"
        </div>
      )}
    </div>
  )
}
